/// <reference lib="es2021.string" />
import Flow, { FlowSpec } from '@digibee/flow';

import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import { getFeatureFlag } from '~/common/helpers/globalFeatureFlags';
import { slugify } from '~/common/helpers/string';

type Connector = {
  jsonSchema?: Array<{
    schema?: Array<{
      property?: string;
      default?: string;
    }>;
  }>;
  type?: string;
};

export const DEFAULT_DOUBLE_BRACES_ALIAS_NAME = 'undefined-name-##';

export const DOUBLE_BRACES_ALIAS_IDENTIFIER_PATTERN =
  '^[a-z](?:[a-z0-9-]*[a-z0-9])?$';

export const DOUBLE_BRACES_ALIAS_IDENTIFIER_REGEX = new RegExp(
  DOUBLE_BRACES_ALIAS_IDENTIFIER_PATTERN
);

export const MAX_ALIAS_LENGTH = 30;
export const MIN_ALIAS_LENGTH = 3;

export const isValidDoubleBracesAliasIdentifier = (alias: string): boolean =>
  alias.length >= MIN_ALIAS_LENGTH &&
  alias.length <= MAX_ALIAS_LENGTH &&
  DOUBLE_BRACES_ALIAS_IDENTIFIER_REGEX.test(alias);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getDoubleBracesAlias = (component: any): string | undefined =>
  component?.doubleBracesAlias;

export const getAllDoubleBracesAlias = (flow: Flow): Set<string> => {
  const aliases: Set<string> = new Set();

  flow.components().forEach(component => {
    const spec = component.spec();
    const doubleBracesAlias = getDoubleBracesAlias(spec);

    if (doubleBracesAlias) {
      aliases.add(doubleBracesAlias);
    }
  });

  return aliases;
};

const RESERVED_CHARS = 3;
const FALLBACK_ALIAS_BASE = 'step';
const LEADING_DIGIT_PREFIX = 'a';
const HYPHEN = '-';

const isDigit = (char: string) => char >= '0' && char <= '9';

const isLowerLetter = (char: string | undefined) =>
  char !== undefined && char >= 'a' && char <= 'z';

const isAliasChar = (char: string) =>
  char === HYPHEN || isDigit(char) || isLowerLetter(char);

const trimTrailingHyphens = (value: string): string => {
  let end = value.length;

  while (end > 0 && value[end - 1] === HYPHEN) {
    end -= 1;
  }

  return value.slice(0, end);
};

const normalizeAliasBase = (baseAlias: string): string => {
  const lower = baseAlias.toLowerCase();
  const chars: string[] = [];

  for (let index = 0; index < lower.length; index += 1) {
    const normalized = isAliasChar(lower[index]) ? lower[index] : HYPHEN;
    const previous = chars[chars.length - 1];
    const isSkippableHyphen =
      normalized === HYPHEN && (previous === undefined || previous === HYPHEN);

    if (!isSkippableHyphen) {
      chars.push(normalized);
    }
  }

  return trimTrailingHyphens(chars.join(''));
};

const sanitizeAliasBase = (baseAlias: string): string => {
  const maxBaseLength = MAX_ALIAS_LENGTH - RESERVED_CHARS;
  let sanitized = normalizeAliasBase(baseAlias);

  if (!isLowerLetter(sanitized[0])) {
    sanitized = `${LEADING_DIGIT_PREFIX}${sanitized}`;
  }

  if (sanitized.length > maxBaseLength) {
    sanitized = trimTrailingHyphens(sanitized.slice(0, maxBaseLength));
  }

  if (!isLowerLetter(sanitized[0])) {
    return FALLBACK_ALIAS_BASE;
  }

  return sanitized;
};

const generateUniqueAliasWithSuffix = (
  baseAliasWithoutSuffix: string,
  existingDoubleBracesAlias: Set<string>
): string => {
  let suffix = 1;
  let uniqueAlias = `${baseAliasWithoutSuffix}-${suffix}`;

  while (existingDoubleBracesAlias.has(uniqueAlias)) {
    suffix += 1;
    uniqueAlias = `${baseAliasWithoutSuffix}-${suffix}`;
  }

  return uniqueAlias;
};

export const generateUniqueAlias = (
  baseAlias: string,
  existingDoubleBracesAlias: Set<string>
): string => {
  let baseAliasWithoutSuffix: string;

  if (baseAlias.endsWith('-##')) {
    baseAliasWithoutSuffix = baseAlias.replace('-##', '');
  } else {
    const numericSuffixMatch = baseAlias.match(/^(.*?)-([0-9]+)$/);
    baseAliasWithoutSuffix = numericSuffixMatch
      ? numericSuffixMatch[1]
      : baseAlias;
  }

  return generateUniqueAliasWithSuffix(
    sanitizeAliasBase(baseAliasWithoutSuffix),
    existingDoubleBracesAlias
  );
};

// TODO review - is this used?!

// export const generateUniqueDoubleBracesAlias = (
//   baseAlias: string,
//   flow: Flow
// ): string => {
//   const existingDoubleBracesAlias = getAllDoubleBracesAlias(flow);
//   return generateUniqueAlias(baseAlias, existingDoubleBracesAlias);
// };

export const hasDoubleBracesAlias = (
  connector: Connector | null | undefined
): boolean => {
  if (!connector) return false;

  if (connector.type === 'capsule') {
    return true;
  }

  if (connector.jsonSchema) {
    return connector.jsonSchema.some(
      (section: { schema?: Array<{ property?: string }> }) =>
        section.schema &&
        section.schema.some(
          (field: { property?: string }) =>
            field.property === 'doubleBracesAlias'
        )
    );
  }

  return false;
};

export const generateDoubleBracesAliasForComponent = (
  component: {
    stepName?: string;
    type: string;
    name?: string;
    doubleBracesAlias?: string;
  },
  connectorsList: { components: Connector[] },
  getConnector: (type: string, name: string) => Connector | null | undefined,
  existingDoubleBracesAlias: Set<string>
): string | null => {
  const componentConnector =
    component.type === 'capsule'
      ? component
      : getConnector(component.type, component.name || '');

  const supportsDoubleBracesAlias = hasDoubleBracesAlias(componentConnector);

  if (supportsDoubleBracesAlias) {
    let baseAlias = '';

    if (componentConnector?.type === 'capsule') {
      baseAlias =
        // @ts-expect-error name property does not exist on Connector
        slugify(componentConnector.stepName) ||
        DEFAULT_DOUBLE_BRACES_ALIAS_NAME;
    } else {
      const connectorSchema = connectorsList.components.find(
        // @ts-expect-error name property does not exist on Connector
        (connector: Connector) => connector.name === componentConnector?.name
      );

      if (
        connectorSchema?.jsonSchema &&
        Array.isArray(connectorSchema.jsonSchema)
      ) {
        const aliasField = connectorSchema.jsonSchema
          .flatMap(section =>
            section && Array.isArray(section.schema) ? section.schema : []
          )
          .find(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (field: any) =>
              field.property === 'doubleBracesAlias' &&
              typeof field.default === 'string'
          );

        baseAlias =
          slugify(component.doubleBracesAlias || '') ||
          aliasField?.default ||
          DEFAULT_DOUBLE_BRACES_ALIAS_NAME;
      }
    }

    const uniqueAlias = generateUniqueAlias(
      baseAlias,
      existingDoubleBracesAlias
    );

    return uniqueAlias;
  }

  return null;
};

export const collectDoubleBracesAliasFromFlowSpec = (
  flowSpec: FlowSpec
): Set<string> => {
  const aliases: Set<string> = new Set();

  if (!flowSpec) return aliases;

  Object.values(flowSpec).forEach(trackComponents => {
    trackComponents.forEach(component => {
      const doubleBracesAlias = getDoubleBracesAlias(component);
      if (doubleBracesAlias) {
        aliases.add(doubleBracesAlias);
      }
    });
  });

  return aliases;
};

type ProcessRawFlowSpecParams = {
  flowSpec?: FlowSpec;
  currentFlowSpec: FlowSpec;
  connectorsList: { components: Connector[] };
  getConnector: (type: string, name: string) => Connector | null | undefined;
  processType: 'update' | 'merge';
};

/**
 * On paste ('merge') the alias must be a valid identifier, so invalid ones are
 * regenerated. On load / trigger change ('update') an alias already saved in the
 * pipeline is kept as is (only template or out-of-range aliases are regenerated):
 * renaming it would silently break `{{ step.<alias>.* }}` references in other
 * steps, and the invalid identifier is surfaced as a linter warning instead.
 */
const shouldKeepExistingAlias = (
  alias: string,
  processType: ProcessRawFlowSpecParams['processType']
): boolean => {
  if (processType === 'merge') {
    return isValidDoubleBracesAliasIdentifier(alias);
  }

  return (
    !alias.endsWith('-##') &&
    alias.length >= MIN_ALIAS_LENGTH &&
    alias.length <= MAX_ALIAS_LENGTH
  );
};

export const ensureDoubleBracesAliasesInFlowSpec = ({
  flowSpec = {},
  currentFlowSpec,
  connectorsList,
  getConnector,
  processType
}: ProcessRawFlowSpecParams): FlowSpec => {
  const isDoubleBracesAliasEnabled = getFeatureFlag(
    featureFlagConstants.BUILD_ENABLE_DOUBLE_BRACES_ALIAS
  );
  const isCapsuleDoubleBracesAliasEnabled = getFeatureFlag(
    featureFlagConstants.BUILD_ENABLE_CAPSULES_DOUBLE_BRACES_ALIAS
  );
  if (!isDoubleBracesAliasEnabled) {
    return { ...currentFlowSpec, ...flowSpec };
  }

  const existingDoubleBracesAlias =
    processType === 'update'
      ? new Set<string>()
      : collectDoubleBracesAliasFromFlowSpec(currentFlowSpec);

  const processedFlowSpec =
    processType === 'update' ? { ...currentFlowSpec } : { ...flowSpec };

  Object.keys(processedFlowSpec).forEach(trackKey => {
    processedFlowSpec[trackKey] = processedFlowSpec[trackKey].map(component => {
      if (component.type === 'capsule' && !isCapsuleDoubleBracesAliasEnabled) {
        return component;
      }
      const doubleBracesAlias = getDoubleBracesAlias(component);

      const isValidExistingAlias =
        doubleBracesAlias &&
        shouldKeepExistingAlias(doubleBracesAlias, processType) &&
        !existingDoubleBracesAlias.has(doubleBracesAlias);

      if (isValidExistingAlias) {
        existingDoubleBracesAlias.add(doubleBracesAlias!);
        return component;
      }
      const newDoubleBracesAlias = generateDoubleBracesAliasForComponent(
        component,
        connectorsList,
        getConnector,
        existingDoubleBracesAlias
      );

      if (newDoubleBracesAlias) {
        existingDoubleBracesAlias.add(newDoubleBracesAlias);

        return {
          ...component,
          doubleBracesAlias: newDoubleBracesAlias
        };
      }

      return component;
    });
  });

  return { ...currentFlowSpec, ...processedFlowSpec };
};
